<template>
  <article v-if="entries.length > 0">
    <vue-glide ref="slider" v-model="activeSlide" type="carousel" class="max-w-full" :per-view="perView" :breakpoints="breakpoints">
      <vue-glide-slide v-for="(entry, index) in amendedEntries" :key="index">
        <div class="w-full bg-app-green-200 rounded-xl overflow-hidden">
          <AtomLink class="inline-block w-full h-full" :url="get(entry, 'reference.url')">
            <picture v-if="entry.image && entry.image.img">
              <source v-for="(source, key) in entry.image.sources" :key="key" :media="source.media" :width="source.width" :height="source.height" :srcset="source.src" />
              <img :width="entry.image.img.width" :height="entry.image.img.height" :src="entry.image.img.src" :alt="entry.image.source || entry.image.title" />
            </picture>
            <div v-else-if="get(entry, 'youtube.code')" class="aspect-video youtube">
              <div class="w-full h-full" v-html="entry.youtube.code" />
            </div>
            <template v-if="entry.subtitle || entry.introduction">
              <div class="block border-b-2 border-app-lime" />
              <div class="px-6 py-5">
                <div>
                  <div v-if="entry.subtitle" class="mb-4 pb-4 px-5 font-bold border-b border-white">{{ entry.subtitle }}</div>
                  <div v-if="entry.introduction" class="px-5" v-html="entry.introduction" />
                </div>
              </div>
            </template>
          </AtomLink>
        </div>
      </vue-glide-slide>
    </vue-glide>

    <div class="px-6 md:px-0">
      <button
        class="border-box float-left mt-4 w-2/5 cursor-pointer focus:ring-1 focus:ring-white focus:ring-offset-1 focus:rounded-md hover:text-app-green text-black transform transition duration-200"
        :aria-label="t_('prev')"
        @click="slider.glide.go('<')"
      >
        <AtomIconArrowLeft class="float-left w-4 md:float-right" />
      </button>
      <div class="border-box inline-block mt-3 w-1/5 text-center font-bold">{{ activeSlide + 1 }}/{{ entries.length }}</div>
      <button
        class="border-box float-right mt-4 w-2/5 cursor-pointer focus:ring-1 focus:ring-white focus:ring-offset-1 focus:rounded-md hover:text-app-green text-black transform transition duration-200"
        :aria-label="t_('next')"
        @click="slider.glide.go('>')"
      >
        <AtomIconArrowRight class="float-right w-4 md:float-left" />
      </button>
    </div>
  </article>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { getMediaImage } from '~/composables/media'
import { setupTranslationPrefixer } from '~/composables/util'
import get from 'lodash/get'

//
// For vue-glide documentation, see https://antonreshetov.github.io/vue-glide/props/
//

interface Props {
  entries: Array<any>
  breakpoints?: Record<number, Record<string, any>>
  perView?: number
}

const props = withDefaults(defineProps<Props>(), {
  breakpoints: () => ({
    800: { peek: { before: 0, after: 0 } },
    999999: { peek: { before: 100, after: 100 } },
  }),
  perView: 1,
})

const slider = ref()
const activeSlide = ref(0)

const amendedEntries = props.entries.map((entry) => ({ ...entry, image: getMediaImage(entry.image) }))

const { t_ } = setupTranslationPrefixer('carousel')
</script>

<style lang="scss" scoped>
@import '@glidejs/glide/dist/css/glide.core';
@import '@glidejs/glide/dist/css/glide.theme';

.youtube iframe {
  @apply w-full h-full;
}

.glide__slide {
  transition: 0.2s ease-in-out all;
}

.glide__slide:not(.glide__slide--active) {
  opacity: 0.4;
  transform: scale(0.9);
}
</style>
